import React, { useState, useEffect, useMemo } from "react";
import "./Clients.css";
import axiosInstance from "../../../Config/axiosInstance";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import AddClientContacts from "./AddClientContacts";
import AddClient from "./AddClient";
import EditClient from "./EditClient";
import ClientContacts from "./ClientContacts";
import ClientDeleteDialog from "./ClientDeleteDialog";

// Normalizează un URL și întoarce doar path-ul.
const getPathFromUrl = (url) => {
  try {
    if (url.startsWith("/")) return url;
    const { pathname } = new URL(url);
    return pathname;
  } catch (error) {
    console.error("Eroare la extragerea URL-ului:", error);
    return url;
  }
};

// Componentă simplă pentru titlul din header-ul paginii.
const HeaderTitle = (props) => <p className={props.className}>{props.title}</p>;

// Butonul principal de adăugare client din header.
const HeaderAddButton = ({ onClick }) => (
  <Button
    variant="contained"
    color="primary"
    onClick={onClick}
    startIcon={<AddIcon />}
    style={{ cursor: "pointer" }}
  >
    Add Client
  </Button>
);

// Badge vizual pentru statusul clientului.
const StatusPill = ({ value }) => {
  let statusClass = "status-default";
  let statusLabel = value || "";

  if (value === "Active") {
    statusClass = "status-active";
    statusLabel = "Active";
  } else if (value === "Inactive") {
    statusClass = "status-inactive";
    statusLabel = "Inactive";
  } else if (value === "Prospect") {
    statusClass = "status-prospect";
    statusLabel = "Prospect";
  } else if (value === "Blocked") {
    statusClass = "status-blocked";
    statusLabel = "Blocked";
  }

  return <span className={`status-pill ${statusClass}`}>{statusLabel}</span>;
};

const Client = () => {
  // State-ul principal pentru rândurile din tabel și popup-urile de lucru.
  const [rows, setRows] = useState([]);
  const [isAddPopupOpen, setAddPopupOpen] = useState(false);
  const [isEditPopupOpen, setEditPopupOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);

  // Permisiunile utilizatorului pentru pagina curentă.
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // State pentru gestionarea contactelor clientului.
  const [isContactPopupOpen, setContactPopupOpen] = useState(false);
  const [createdClientId, setCreatedClientId] = useState(null);
  const [isViewClientContactsOpen, setViewClientContactsOpen] = useState(false);
  const [selectedClientContacts, setSelectedClientContacts] = useState([]);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [currentClientId, setCurrentClientId] = useState(null);

  // State pentru dialogul de ștergere client.
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // La montarea componentei:
  // 1. citește permisiunile din localStorage
  // 2. stabilește ce acțiuni sunt permise pe pagina /clients
  // 3. încarcă lista de clienți din backend
  useEffect(() => {
    const requiredUrl = getPathFromUrl("/clients");
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(
      permissions.some(
        (page) =>
          getPathFromUrl(page.url) === requiredUrl && page.can_create === true
      )
    );

    setCanEdit(
      permissions.some(
        (page) =>
          getPathFromUrl(page.url) === requiredUrl && page.can_edit === true
      )
    );

    setCanDelete(
      permissions.some(
        (page) =>
          getPathFromUrl(page.url) === requiredUrl && page.can_delete === true
      )
    );

    fetchClients();
  }, []);

  // Funcția principală de încărcare a clienților.
  // Pentru fiecare client:
  // 1. se cer și contactele asociate
  // 2. contactele sunt atașate obiectului client
  // 3. lista finală este sortată astfel încât clienții activi să apară primii,
  //    iar în interiorul listei cele mai noi înregistrări să fie sus.
  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get("/clients/");

      const clientsWithContacts = await Promise.all(
        (response.data || []).map(async (client) => {
          try {
            const contactsResponse = await axiosInstance.get(
              `/client-contacts/?client=${client.id}`
            );

            let clientContacts = [];
            if (contactsResponse.data && Array.isArray(contactsResponse.data)) {
              clientContacts = contactsResponse.data.filter(
                (contact) =>
                  contact.client === client.id ||
                  contact.client_id === client.id ||
                  (contact.client && contact.client.id === client.id)
              );
            }

            return {
              ...client,
              contacts: clientContacts,
            };
          } catch (error) {
            console.error(
              `Error fetching contacts for client ${client.id}:`,
              error
            );
            return { ...client, contacts: [] };
          }
        })
      );

      const sortedClients = clientsWithContacts.sort((a, b) => {
        if (a.status === "Inactive" && b.status !== "Inactive") return 1;
        if (a.status !== "Inactive" && b.status === "Inactive") return -1;
        return b.id - a.id;
      });

      setRows(sortedClients);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Deschide/închide popup-ul de adăugare client.
  const toggleAddPopup = () => {
    setAddPopupOpen((prev) => !prev);
  };

  // Deschide popup-ul de editare și setează clientul selectat.
  const toggleEditPopup = (client) => {
    setClientToEdit(client);
    setEditPopupOpen(true);
  };

  // Închide popup-ul de adăugare.
  const handleCloseAddPopup = () => {
    setAddPopupOpen(false);
  };

  // Închide popup-ul de editare și golește clientul selectat.
  const handleCloseEditPopup = () => {
    setEditPopupOpen(false);
    setClientToEdit(null);
  };

  // Deschide popup-ul de adăugare contact.
  const handleAddContact = () => {
    setContactPopupOpen(true);
  };

  // Închide popup-ul de adăugare contact.
  const handleCloseContactPopup = () => {
    setContactPopupOpen(false);
  };

  // După adăugarea unui contact, reîncarcă lista de clienți
  // pentru a sincroniza datele afișate.
  const handleAddContactToList = async () => {
    await fetchClients();
  };

  // Deschide modalul de vizualizare contacte pentru clientul selectat.
  // Salvează contactele, numele clientului și id-ul clientului curent.
  const handleViewContacts = (client) => {
    setSelectedClientContacts(client.contacts || []);
    setSelectedClientName(client.name);
    setCurrentClientId(client.id);
    setViewClientContactsOpen(true);
  };

  // Închide modalul de vizualizare contacte, resetează selecția
  // și reîncarcă datele pentru a avea lista actualizată.
  const handleCloseViewClientContacts = async () => {
    setViewClientContactsOpen(false);
    setSelectedClientContacts([]);
    setSelectedClientName("");
    setCurrentClientId(null);
    await fetchClients();
  };

  // Reîncarcă lista de clienți după editarea unui contact.
  // Dacă fereastra de vizualizare contacte este încă deschisă,
  // se cere din nou lista de contacte pentru clientul activ.
  const handleContactUpdated = async () => {
    await fetchClients();

    if (isViewClientContactsOpen && currentClientId) {
      try {
        const contactsResponse = await axiosInstance.get(
          `/client-contacts/?client=${currentClientId}`
        );

        let updatedContacts = [];
        if (contactsResponse.data && Array.isArray(contactsResponse.data)) {
          updatedContacts = contactsResponse.data.filter(
            (contact) =>
              contact.client === currentClientId ||
              contact.client_id === currentClientId ||
              (contact.client && contact.client.id === currentClientId)
          );
        }

        setSelectedClientContacts(updatedContacts);
      } catch (error) {
        console.error("Error refreshing contacts:", error);
      }
    }
  };

  // Deschide dialogul de ștergere și setează clientul selectat.
  const handleOpenDeleteDialog = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  // Închide dialogul de ștergere și resetează clientul selectat.
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  // După ștergerea cu succes a unui client,
  // închide dialogul și reîncarcă lista.
  const handleClientDeleted = async () => {
    handleCloseDeleteDialog();
    await fetchClients();
  };

  // Definește coloanele tabelului principal.
  // Coloanele de edit/delete sunt adăugate doar dacă utilizatorul are permisiuni.
  const columns = useMemo(() => {
    const cols = [
      {
        field: "client_code",
        headerName: "Client Code",
        minWidth: 140,
        flex: 1,
      },
      {
        field: "name",
        headerName: "Client",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "address",
        headerName: "Address",
        minWidth: 220,
        flex: 1.4,
      },
      {
        field: "phone",
        headerName: "Phone",
        minWidth: 140,
        flex: 1,
      },
      {
        field: "cui",
        headerName: "CUI",
        minWidth: 140,
        flex: 1,
      },
      {
        field: "responsible_person_label",
        headerName: "Responsible Person",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "contacts",
        headerName: "Contact Persons",
        minWidth: 140,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,

        // Afișează butonul de vizualizare contacte doar dacă există contacte asociate.
        renderCell: (params) =>
          params.value && params.value.length > 0 ? (
            <IconButton
              onClick={() => handleViewContacts(params.row)}
              sx={{ color: "#424242" }}
              title="View contacts"
            >
              <VisibilityIcon />
            </IconButton>
          ) : null,
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 150,

        // Afișează statusul sub formă de badge.
        renderCell: (params) => <StatusPill value={params.value} />,
      },
    ];

    if (canEdit || canDelete) {
      cols.push({
        field: "actions",
        headerName: "Action",
        width: 140,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",

        // Coloana de acțiuni afișează edit și/sau delete
        // în funcție de permisiunile utilizatorului.
        renderCell: (params) => (
          <>
            {canEdit && (
              <IconButton
                onClick={() => toggleEditPopup(params.row)}
                sx={{ color: "#1976d2" }}
                title="Edit client"
              >
                <EditNoteSharpIcon />
              </IconButton>
            )}

            {canDelete && (
              <IconButton
                onClick={() => handleOpenDeleteDialog(params.row)}
                sx={{ color: "#d32f2f" }}
                title="Delete client"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        ),
      });
    }

    return cols;
  }, [canEdit, canDelete]);

  return (
    <div className="clients-page">
      {/* Header-ul paginii, cu titlu și buton de adăugare dacă există permisiune */}
      <div className="header-container reg-header">
        <HeaderTitle className="leftside-title" title="CLIENTS" />
        {canCreate ? <HeaderAddButton onClick={toggleAddPopup} /> : null}
      </div>

      {/* Tabelul principal cu lista clienților */}
      <div className="main-container">
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 12, 20, 25]}
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.4",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              whiteSpace: "normal",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important",
            },
          }}
        />
      </div>

      {/* Popup pentru adăugare client */}
      {isAddPopupOpen && (
        <AddClient
          isOpen={isAddPopupOpen}
          onClose={handleCloseAddPopup}
          fetchClients={fetchClients}
          setCreatedClientId={setCreatedClientId}
        />
      )}

      {/* Popup pentru editare client */}
      {isEditPopupOpen && clientToEdit && (
        <EditClient
          isOpen={isEditPopupOpen}
          onClose={handleCloseEditPopup}
          client={clientToEdit}
          onAddContact={handleAddContact}
          fetchClients={fetchClients}
        />
      )}

      {/* Popup pentru adăugare contact */}
      <AddClientContacts
        isOpen={isContactPopupOpen}
        onClose={handleCloseContactPopup}
        onAddContact={handleAddContactToList}
        clientId={clientToEdit?.id || createdClientId}
        onSuccess={fetchClients}
      />

      {/* Modal pentru vizualizarea contactelor unui client */}
      <ClientContacts
        isOpen={isViewClientContactsOpen}
        onClose={handleCloseViewClientContacts}
        contacts={selectedClientContacts}
        clientName={selectedClientName}
        clientId={currentClientId}
        onContactUpdated={handleContactUpdated}
      />

      {/* Dialog pentru ștergerea unui client */}
      <ClientDeleteDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        client={clientToDelete}
        onDeleted={handleClientDeleted}
      />
    </div>
  );
};

export default Client;